import React, {
	forwardRef,
	useRef,
	useState,
	useMemo,
	useEffect,
	useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import classNames from 'classnames';
import { SearchIcon } from 'components/icons';
import { MuiCustomTooltip } from 'components/base/tooltip';
import { Loader } from 'components/loader/Loader';
import { CustomInput } from 'components/base/input/CustomInput';
import { compareStrings } from 'components/base/helpers';
import { TooltipPositionType } from 'components/base/tooltip/Tooltip';
import {
	CHECK_ALL_OPTION_VALUE,
	ISelectOption,
} from 'components/base/model/ISelect';

import './selectField.styles.scss';

export interface CustomSelectProps {
	optionsHeader?: string | JSX.Element;
	options?: ISelectOption[];
	value?: string | number | (string | number)[];
	name: string;
	wrapperClass?: string;
	error?: string;
	withSearch?: boolean;
	multiple?: boolean;
	renderOption?: (i: ISelectOption, isChecked?: boolean) => JSX.Element;
	renderTitle?: (i: ISelectOption) => JSX.Element;
	onChange?: (a?: string | string[] | number) => void;
	label?: string;
	hintText?: string;
	id?: string;
	required?: boolean;
	disabled?: boolean;
	checkAllTitle?: string;
	isLoading?: boolean;
	isOptionsLoading?: boolean;
	onChangeFilterValue?: (p: string) => void;
	hidden?: boolean;
	defaultValue?: string | number | (string | number)[];
	tooltipText?: string;
	tooltipPosition?: TooltipPositionType;
	filterCheckbox?: React.FC;
}

export const CustomSelect = forwardRef<HTMLSelectElement, CustomSelectProps>(
	(
		{
			optionsHeader,
			wrapperClass,
			error,
			withSearch,
			name,
			renderOption,
			multiple,
			options = [],
			value,
			onChange,
			required,
			id,
			label,
			hintText,
			disabled = false,
			renderTitle,
			checkAllTitle,
			isLoading,
			isOptionsLoading,
			onChangeFilterValue,
			hidden,
			defaultValue,
			tooltipText,
			tooltipPosition,
			filterCheckbox = () => null,
		},
		ref,
	) => {
		const { t } = useTranslation('common');
		const { t: te } = useTranslation('errors');

		const dropdownListRef = useRef<HTMLUListElement>(null);

		const [isOpened, setIsOpened] = useState(false);

		const [errorText, setErrorText] = useState<string>();

		const [optionsList, setOptionsList] = useState<ISelectOption[]>(options);
		const [activeOptionsList, setActiveOptionsList] = useState<ISelectOption[]>(
			[],
		);

		const [activeOptionValues, setActiveOptionValues] = useState<string[]>([]);
		const [filterValue, setFilterValue] = useState('');
		const activeHeader = useMemo(() => {
			const titleList = activeOptionsList?.map(({ title, value: val }) =>
				typeof renderTitle === 'function'
					? renderTitle({ title, value: val })
					: title,
			);

			return typeof renderTitle === 'function'
				? titleList
				: titleList.join(', ');
		}, [activeOptionsList, renderTitle]);

		const handleClick = useCallback(
			(e: MouseEvent) => {
				const target = e.target as HTMLElement;
				const dropdown = dropdownListRef.current;

				if (isOpened && !dropdown?.contains(target)) {
					setIsOpened(false);
				}
			},
			[dropdownListRef, isOpened],
		);

		useEffect(() => {
			setErrorText(error);
		}, [error]);

		useEffect(() => {
			if (options?.length) {
				setOptionsList(options);
			}
		}, [options]);

		useEffect(() => {
			if (!value) return setActiveOptionValues([]);
			if (Array.isArray(value)) {
				const isAllChecked =
					value.length ===
					options.filter(({ value: v }) => v !== CHECK_ALL_OPTION_VALUE).length;
				isAllChecked
					? setActiveOptionValues([
							CHECK_ALL_OPTION_VALUE,
							...value.map((v) => v.toString()),
					  ])
					: setActiveOptionValues(value.map((v) => v.toString()));
			} else {
				setActiveOptionValues([value.toString()]);
			}
		}, [optionsList, value]);

		useEffect(() => {
			if (activeOptionValues.length) {
				setActiveOptionsList(
					options.filter(({ value: newValue }) =>
						activeOptionValues.includes(newValue.toString()),
					),
				);
				if (required && isOpened) setErrorText('');
			} else {
				setActiveOptionsList([]);
				if (required && isOpened) setErrorText(te('required'));
			}
		}, [activeOptionValues]);

		useEffect(() => {
			if (isOpened) {
				window.addEventListener('click', handleClick);
			} else {
				window.removeEventListener('click', handleClick);
			}
		}, [isOpened, handleClick]);

		useEffect(() => {
			if (filterValue) {
				setOptionsList(
					options.filter(({ title }) => {
						if (typeof title === 'object') {
							for (const titleFieldValue of Object(title).values()) {
								const hasСoincidence = compareStrings(
									`${titleFieldValue}`,
									filterValue,
								);

								if (hasСoincidence) return true;
							}
							return false;
						}

						return compareStrings(`${title}`, filterValue);
					}),
				);
			} else {
				setOptionsList(options);
			}
		}, [filterValue]);

		const handleFilterChange = ({
			target: { value: newValue },
		}: React.ChangeEvent<HTMLInputElement>) => {
			setFilterValue(newValue);
			if (typeof onChangeFilterValue === 'function') {
				onChangeFilterValue(newValue);
			}
		};

		const onChangeOption = (newValue: string | number) => {
			const v = newValue.toString();
			let res;

			if (v === CHECK_ALL_OPTION_VALUE) {
				if (
					activeOptionsList.length ===
					optionsList.filter(({ value: val }) => val !== CHECK_ALL_OPTION_VALUE)
						.length
				) {
					res = [];
				} else {
					res = options.map(({ value: val }) => val.toString());
				}
			} else if (!multiple) {
				res = v;
			} else {
				res = activeOptionValues.filter((activeValue) => activeValue !== v);

				res =
					res.length === activeOptionValues.length
						? [...activeOptionValues, v]
						: res.filter((val) => val !== CHECK_ALL_OPTION_VALUE);
			}

			if (onChange) {
				onChange(res.length ? res : undefined);
			}

			if (!multiple) {
				setIsOpened(false);
			}
		};

		const toggleList = (e: React.MouseEvent) => {
			if (!disabled) {
				e.stopPropagation();
				setIsOpened(!isOpened);
			}
		};

		return (
			<div
				className={classNames('group', wrapperClass, {
					'group--error': !!errorText,
					'group--active': isOpened || activeOptionValues.length,
					'group--opened': isOpened,
					hidden,
				})}
			>
				<div
					data-role="custom-select"
					className="group__select"
					onClick={toggleList}
					// eslint-disable-next-line
					tabIndex={1}
				>
					<Loader isLoading={isLoading}>
						<select
							name={name}
							multiple={multiple}
							hidden
							ref={ref}
							disabled={true}
						/>
						{activeOptionsList.length ? (
							<div
								className="group__select-values"
								title={activeHeader as string}
							>
								{activeHeader}
							</div>
						) : (
							<span
								style={{
									color: 'transparent',
									userSelect: 'none',
								}}
							>
								p
							</span>
						)}
					</Loader>
				</div>
				<div
					className="select__label-bg"
					data-label={required ? `${label}*` : label}
				/>
				<label className="select__label" htmlFor={id}>
					{required ? `${label}*` : label}
				</label>
				{isOpened && (
					<ul className="select-dropdown" ref={dropdownListRef}>
						<div className="select-dropdown__search-wrapper">
							{filterCheckbox({})}
							{withSearch && (
								<div className="select-dropdown__search-input">
									<CustomInput
										name={`${name}--filter`}
										label={t('search')}
										id={`${name}-filter`}
										value={filterValue}
										onChange={handleFilterChange}
										wrapperClass="select-dropdown__search"
									/>
									<SearchIcon iconClass="select-dropdown__search-icon" />
								</div>
							)}
						</div>
						{optionsHeader && (
							<div className="select-dropdown__header">{optionsHeader}</div>
						)}
						{checkAllTitle && !filterValue && (
							<SelectOption
								value={CHECK_ALL_OPTION_VALUE}
								key={CHECK_ALL_OPTION_VALUE}
								renderOption={renderOption}
								isChecked={activeOptionValues.includes(CHECK_ALL_OPTION_VALUE)}
								onClickOption={onChangeOption}
								title={checkAllTitle}
							/>
						)}
						{optionsList.map((optionItem) => {
							return (
								<SelectOption
									{...optionItem}
									key={optionItem.value}
									renderOption={renderOption}
									isChecked={activeOptionValues.includes(
										optionItem.value.toString(),
									)}
									onClickOption={onChangeOption}
									title={optionItem.title}
								/>
							);
						})}
						{isOptionsLoading && (
							<li className="select-dropdown__item">
								<Loader isLoading={true} />
							</li>
						)}
					</ul>
				)}
				<p className="group__hint font-caption">{errorText || hintText}</p>
				{tooltipText && (
					<MuiCustomTooltip title={tooltipText} className="group__tooltip" />
				)}
			</div>
		);
	},
);

interface SelectOptionProps extends ISelectOption {
	onClickOption: (value: string | number) => void;
	isChecked?: boolean;
	renderOption?: (i: ISelectOption, isChecked?: boolean) => JSX.Element;
}

function SelectOption({
	title,
	value,
	onClickOption,
	isChecked,
	renderOption,
}: SelectOptionProps) {
	function handleClick() {
		onClickOption(value);
	}

	return (
		<li
			className={classNames('select-dropdown__item', {
				'select-dropdown__item--active': isChecked,
			})}
			value={value}
			onClick={handleClick}
			// eslint-disable-next-line
			tabIndex={1}
		>
			{renderOption
				? renderOption({ value, title: title as string }, isChecked)
				: title}
		</li>
	);
}

export function createOptionListFromEnum<T>(
	list: T,
	t: TFunction,
): ISelectOption[] {
	return (Object.values(list).reduce(
		(res: Array<Record<string, string>>, value) => {
			res.push({ title: t(value.toLowerCase()), value });
			return res;
		},
		[],
	) as unknown) as ISelectOption[];
}

export function createOptionList<
	T extends { [key: string]: string | number | any }
>(
	array: Array<T> | undefined = [],
	titleKey: keyof T = 'title',
	valueKey: keyof T = 'value',
	optionTitle: keyof T = 'optionTitle',
): ISelectOption[] {
	return array
		.map((el) => ({
			title: el[titleKey] as string,
			optionTitle: el[optionTitle] as string,
			value: el[valueKey],
		}))
		.filter(({ value }) => !!value || value === 0);
}
